"use client";
import React, { useEffect, useState } from "react";
import { Box, Container } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useRouter } from "next/navigation";

import i18n from "../i18n";

// ─── Styled Components (same pattern as LeappageNavbar) ───────────────────────
const StyledFooter = styled(Box)(({ theme }) => ({
  backgroundColor: "#0B0A10",
  width: "100%",
  display: "flex",
  alignItems: "center",
  minHeight: "90px",
  [theme.breakpoints.down("lg")]: {
    minHeight: "60px",
  },
}));

const LogoContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
}));

const CopyrightText = styled(Box)(({ theme }) => ({
  color: "#A6A6A6",
  fontSize: "13px",
  fontWeight: 400,
  fontFamily: "var(--font-outfit)",
  whiteSpace: "nowrap",
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    fontSize: "11px",
  },
}));

const ContactLink = styled("a")(({ theme }) => ({
  color: "#7C3AED",
  fontSize: "13px",
  fontWeight: 500,
  fontFamily: "var(--font-outfit)",
  whiteSpace: "nowrap",
  textDecoration: "none",
  transition: "color 0.2s ease-in-out",
  "&:hover": {
    color: "#9D5CFF",
    textDecoration: "underline",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "11px",
  },
}));

const LEAP_LOGO_URL =
  "https://getgeek-website-assets.b-cdn.net/Getgeek%20Images/GetgeekLogoWhiteOne.webp";

const LeapFooter = () => {
  const router = useRouter();
  const [currentLang, setCurrentLang] = useState("en");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setCurrentLang(i18n.language || "en");

    const handleLangChange = (lng) => setCurrentLang(lng);
    i18n.on("languageChanged", handleLangChange);
    return () => i18n.off("languageChanged", handleLangChange);
  }, []);

  const isRTL = currentLang === "ar";
  const currentYear = new Date().getFullYear();

  return (
    <StyledFooter component="footer">
      <Container
        maxWidth="xl"
        sx={{
          padding: { xs: "0 12px", sm: "0 20px", md: "0 24px", lg: "0 20px" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: { xs: 1.5, sm: 2 },
            py: { xs: 2, sm: 0 },
          }}
        >
          {/* Logo - left side */}
          <LogoContainer >
            <Box
              component="img"
              src={LEAP_LOGO_URL}
              alt="Getgeek Logo"
              sx={{
                width: "auto",
                height: { xs: "22px", sm: "26px", md: "28px" },
                maxWidth: "clamp(70px, 22vw, 100px)",
                objectFit: "contain",
              }}
            />
          </LogoContainer>

          {/* Copyright - center */}
          <CopyrightText
            sx={{
              order: { xs: 3, sm: 2 },
              width: { xs: "100%", sm: "auto" },
              fontFamily: isRTL ? "sans-serif" : "var(--font-outfit)",
            }}
          >
            {mounted
              ? i18n.t("footerLeap.copyright", { year: currentYear })
              : `© ${currentYear} Getgeek. All rights reserved.`}
          </CopyrightText>

          {/* Contact - right side */}
          <ContactLink
            href="mailto:contact@getgeek.ai"
            sx={{
              order: { xs: 2, sm: 3 },
              fontFamily: isRTL ? "sans-serif" : "var(--font-outfit)",
            }}
          >
            {mounted ? i18n.t("footerLeap.contactEmail") : "contact@getgeek.ai"}
          </ContactLink>
        </Box>
      </Container>
    </StyledFooter>
  );
};

export default LeapFooter;